import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import ScrollProgress from "@/components/site/ScrollProgress";
import MobileStickyCTA from "@/components/site/MobileStickyCTA";
import MatrixEngine from "@/components/site/MatrixEngine";
import PatentTech from "@/components/site/PatentTech";
import Partners from "@/components/site/Partners";
import { FadeUp } from "@/components/site/_shared";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { organizationSchema, breadcrumbSchema, SITE_URL, OG_IMAGES } from "@/lib/seo";
import { ACTIVATION_URL } from "@/lib/links";
import { PostIntakeSteps } from "@/components/site/_partials/PostIntakeSteps";

export default function Technology() {
  useDocumentMeta({
    title: "Technology & Patent-Pending IP | Integrated Clinical Intelligence",
    description:
      "The full technology platform behind Integrated Wellness — the Integrated Matrix Engine, IBA 2.0, multi-tissue sensing engines, and the connected health intelligence ecosystem they run on.",
    image: OG_IMAGES.technology,
    url: `${SITE_URL}/technology`,
    schema: [
      organizationSchema(),
      breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Technology", url: "/technology" },
      ]),
    ],
  });

  return (
    <div data-testid="technology-page" className="min-h-screen bg-background">
      <ScrollProgress />
      <Header />
      <main className="pt-28 md:pt-36">
        <section className="relative max-w-5xl mx-auto px-4 md:px-8 pb-3 md:pb-4 text-center">
          <div className="absolute inset-0 iw-grid-bg opacity-30 -z-10" aria-hidden="true" />
          <FadeUp>
            <div className="iw-overline">Integrated Clinical Intelligence</div>
            <h1 className="mt-4 font-display font-bold tracking-tight leading-[1.05] text-[clamp(2rem,4.8vw,3.5rem)] text-primary">
              The Technology Behind{" "}
              <em className="not-italic text-accent">Integrated Clinical Intelligence</em>
            </h1>
            <p className="mt-6 max-w-3xl mx-auto text-lg md:text-xl text-foreground/75 leading-relaxed">
              One system connecting labs, wearables, genetics and longitudinal clinical context.
            </p>
          </FadeUp>
        </section>

        <MatrixEngine compactTop />
        <PatentTech />

        <span id="ecosystem" aria-hidden />
        <Partners />

        <section className="py-20 md:py-28 bg-background">
          <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
            <FadeUp>
              <h2 className="font-display font-bold tracking-tight text-[clamp(1.5rem,3.5vw,2.25rem)] text-primary">
                See what this technology finds in <em className="not-italic text-accent">your</em> data.
              </h2>
              <a
                href={ACTIVATION_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="technology-start-analysis"
                className="mt-8 iw-btn-red inline-flex items-center gap-2 px-9 py-4 text-[0.72rem]"
              >
                Start Your Optimization Analysis <span aria-hidden>→</span>
              </a>
              <PostIntakeSteps testId="technology-post-intake-steps" className="mt-5 justify-center" />
              <p className="mt-8 font-mono text-[0.62rem] uppercase tracking-[0.16em] leading-relaxed text-foreground/45">
                Technologies are patent-pending and offered as clinical decision support — not
                FDA-cleared diagnostic devices.
              </p>
            </FadeUp>
          </div>
        </section>
      </main>
      <Footer />
      <MobileStickyCTA />
    </div>
  );
}
